// Libs
import React, { useCallback, useRef, useEffect } from 'react';
import { Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';

// Components
import SharedKitModal from '../../../components/Kits/Modal/SharedKitModal/SharedKitModal';

// Actions
import { CreateSharedKit, UpdateSharedKit } from '../../../actions/sharedKits';
import { OpenModal, CloseModal } from '../../../actions/modal';
import { Open2Modal, Close2Modal } from '../../../actions/modal2';

// Selectors
import { forOrderSelector } from '../../../store/selectors';

// Styles
import { colors } from '../../../styles/palette';

// Constants
import { NOTIFICATION_MODAL_CLOSE_DELAY_MS } from '../../../constants/modals';

const SHARED_KIT_MODAL_WIDTH_MOBILE = 300;
const SHARED_KIT_MODAL_WIDTH_TABLET = 700;
const SHARED_KIT_MODAL_WIDTH_DESKTOP = 940;

const useSharedKitForm = () => {
  const dispatch = useDispatch();
  const timerRef = useRef(null);
  const theme = useTheme();
  const cartProducts = useSelector((state) => forOrderSelector(state));

  useEffect(() => {
    return () => clearTimeout(timerRef?.current);
  }, []);

  const showNotification = useCallback(
    (message) => {
      dispatch(
        Open2Modal({
          id: 'shared-kit-saved',
          Component: () => <Typography variant="h3">{message}</Typography>,
          closeIcon: false,
          styles: {
            width: 'max-content',
            minHeight: 'initial',
            color: colors.darkGrey,
          },
          props: {
            transitionTimeout: { enter: 0, exit: 500 },
            paddingContent: { xl: '20px', lg: '20px', md: '20px' },
          },
        }),
      );
      timerRef.current = setTimeout(
        () => dispatch(Close2Modal()),
        NOTIFICATION_MODAL_CLOSE_DELAY_MS,
      );
    },
    [dispatch],
  );

  const openModal = useCallback(
    (kit, onClickSave) => {
      dispatch(
        OpenModal({
          Component: SharedKitModal,
          props: {
            kit,
            onClickSave,
            onClickCancel: () => dispatch(CloseModal()),
            paddingContent: {
              xl: '40px 40px 30px',
              lg: '40px 40px 30px',
              md: '24px 16px 24px',
            },
          },
          styles: {
            width: SHARED_KIT_MODAL_WIDTH_DESKTOP,
            [theme.breakpoints.down('lg')]: {
              width: SHARED_KIT_MODAL_WIDTH_TABLET,
            },
            [theme.breakpoints.down('md')]: {
              width: SHARED_KIT_MODAL_WIDTH_MOBILE,
            },
          },
        }),
      );
    },
    [dispatch, theme],
  );

  const onCreateSharedKit = useCallback(() => {
    openModal(null, (kitSettings) =>
      dispatch(
        CreateSharedKit({
          kit: {
            ...kitSettings,
            items: cartProducts.map(({ product, nums }) => ({
              product_id: product,
              quantity: nums,
            })),
          },
          callback: () => showNotification('Added To Shared Marketing Kits'),
        }),
      ),
    );
  }, [dispatch, openModal, cartProducts, showNotification]);

  const onEditSharedKit = useCallback(
    (kit) => {
      openModal(kit, (kitSettings) =>
        dispatch(UpdateSharedKit({ kitId: kit.id, kit: kitSettings })),
      );
    },
    [dispatch, openModal],
  );

  return { onCreateSharedKit, onEditSharedKit };
};

export default useSharedKitForm;
